/**
 * Responsive Sidebar Component
 * Phase 5.4: Mobile Responsiveness
 *
 * Collapsible sidebar with mobile menu support.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  LayoutDashboard,
  Building2,
  Map,
  Flag,
  Webhook,
  BarChart3,
  Mail,
  Settings,
  Key,
  FileText,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';

const navItems = [
  { path: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { path: '/tenants', icon: Building2, labelKey: 'nav.tenants' },
  { path: '/roadmap', icon: Map, labelKey: 'nav.roadmap' },
  { path: '/feature-flags', icon: Flag, labelKey: 'nav.featureFlags' },
  { path: '/api-keys', icon: Key, labelKey: 'nav.apiKeys' },
  { path: '/webhooks', icon: Webhook, labelKey: 'nav.webhooks' },
  { path: '/analytics', icon: BarChart3, labelKey: 'nav.analytics' },
  { path: '/email-templates', icon: Mail, labelKey: 'nav.emailTemplates' },
  { path: '/api-docs', icon: FileText, labelKey: 'nav.apiDocs' },
  { path: '/settings', icon: Settings, labelKey: 'nav.settings' },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

export function Sidebar({ isOpen = true, onClose, isMobile = false }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile && onClose) {
      onClose();
    }
  }, [location.pathname, isMobile, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobile, isOpen, onClose]);

  const sidebarContent = (
    <>
      <div className="flex h-16 items-center justify-between border-b px-6">
        <h1 className="text-xl font-bold">Admin Portal</h1>
        {isMobile && onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close menu"
            className="md:hidden"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1" role="navigation" aria-label="Main navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
    </>
  );

  // Mobile: Overlay sidebar
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={onClose}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-card transition-transform duration-300 ease-in-out md:hidden',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          aria-label="Navigation sidebar"
          aria-hidden={!isOpen}
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // Desktop: Static sidebar
  return (
    <aside
      className="hidden h-full w-64 flex-col border-r bg-card md:flex"
      aria-label="Navigation sidebar"
    >
      {sidebarContent}
    </aside>
  );
}
